import { useCallback, useEffect } from 'react';

import { bindAll } from 'bind-event-listener';

/**
 * Handles user discard actions such as clicking outside the container
 * or pressing the Escape key.
 */
export const useUserDiscard = ({
	ref,
	onDiscard,
}: {
	onDiscard: () => void;
	ref: React.MutableRefObject<HTMLDivElement | null>;
}): void => {
	const handleEscapeKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			onDiscard?.();
		},
		[onDiscard],
	);

	const handleEscapeKeyUp = useCallback((event: KeyboardEvent) => {
		if (event.key !== 'Escape') {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
	}, []);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (ref.current?.contains(event.target as Node)) {
				return;
			}
			onDiscard?.();
		},
		[ref, onDiscard],
	);

	useEffect(() => {
		const contentArea = document.querySelector<HTMLElement>('.ak-editor-content-area');
		if (!contentArea) {
			return;
		}
		// Ensure we focus the content area to capture keyboard events
		contentArea.focus({ preventScroll: true });
		return bindAll(contentArea, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
			{ type: 'click', listener: handleClickOutside },
		]);
	}, [ref, handleEscapeKeyDown, handleEscapeKeyUp, handleClickOutside]);
};
